import { useState, useEffect } from "react";
import { FiDownload, FiUpload } from "react-icons/fi";
import InfiniteScroll from "react-infinite-scroll-component";
import { Trash2 } from "lucide-react";
import * as Tooltip from "@radix-ui/react-tooltip";
import {
  FileUploader,
  RoleValidator,
  ScrollableDropdown,
} from "../../../Components/Common";
import { ExcelFile } from "../../../Assets";
import { useProjects } from "../Hooks/useProjects";
import ProfilePhoto from "../../../Components/Common/ProfilePhoto";
import { toProperCase } from "../../../Utils";
import { ROLE_COLORS } from "../../constants";
import { MdAdd, MdSearch } from "react-icons/md";
import { addProjectUser } from "../../../Services/projectServices";
import toast from "react-hot-toast";
import { TOAST_MESSAGES } from "./constants";
import { getAllUsers } from "../../../Services/userService";
import { ROLES } from "../../../Constants/Strings";
import { extractErrorMessage } from "../../../Utils/extractErrorMessage";

const UsersTab = ({ projectId }) => {
  const {
    onPageChange,
    loading,
    uploadUsersFile,
    deleteUser,
    filters,
    setFilters,
    fetchProjectUsers,
    projectUsers,
    bulkDeleteUsers,
    totalUserCount,
    failedUsers,
    setFailedUsers,
    totalRows
  } = useProjects({ projectId });
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [showUsers, setShowUsers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchText, setSearchText] = useState(filters.search || "");
  const [selectedUserId, setSelectedUserId] = useState(""); // single selection from dropdown
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const loadUsers = async (reset = false) => {
    try {
      if (reset) {
        onPageChange(1);
      } else {
        onPageChange((prev) => prev + 1);
      }

      await fetchProjectUsers();

      setHasMore(projectUsers.length < totalUserCount);
    } catch (err) {
      toast.error(extractErrorMessage(err) || TOAST_MESSAGES.USERS_FETCH_ERROR);
    }
  };

  const handleUpload = async (file) => {
    await uploadUsersFile(file);
    await loadUsers(true);
  };

  const handleDeleteUser = async (userId) => {
    try {
      setDeletingUserId(userId);
      const success = await deleteUser(userId);
      if (success) {
        await loadUsers(true);
      }
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!selectedUserId) {
      toast.error(TOAST_MESSAGES.USER_NOT_SELECTED);
      return;
    }

    if (projectUsers.some((u) => u.id === selectedUserId)) {
      toast.error(TOAST_MESSAGES.DUPLICATE_USER_SELECTED);
      return;
    }

    setSubmitting(true);
    const userPayload = { user_id: selectedUserId };

    try {
      const response = await addProjectUser(projectId, userPayload);
      if (response.status === 201) {
        toast.success(TOAST_MESSAGES.USER_ADD_SUCCESS);
        await loadUsers(true);
        setSelectedUserId("");
        setShowUsers(false);
        setIsUserDropdownOpen(false);
      } else {
        const msg =
          extractErrorMessage(response) || TOAST_MESSAGES.USER_ADD_FAILED;
        toast.error(msg);
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || TOAST_MESSAGES.USER_ADD_FAILED;
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await getAllUsers({
        role: ROLES.PROJECT_MANAGER,
        limit: 0,
      });
      if (response?.data) {
        const formattedUsers = response.data.map((u) => ({
          id: u.id,
          first_name: u.first_name,
          last_name: u.last_name,
        }));
        setUsersList(formattedUsers);
      } else {
        setUsersList([]);
      }
    } catch (err) {
      setUsersList([]);
      toast.error(TOAST_MESSAGES.USERS_FETCH_FAILED);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers((prev) => {
      let newSelection;
      if (prev.includes(userId)) {
        newSelection = prev.filter((id) => id !== userId);
      } else {
        newSelection = [...prev, userId];
      }
      return newSelection;
    });
  };

  const toggleSelectAll = () => {
    const currentPageUserIds = projectUsers.map((u) => u.id);
    const allSelectedOnPage = currentPageUserIds.every((id) =>
      selectedUsers.includes(id)
    );

    if (allSelectedOnPage) {
      setSelectedUsers((prev) =>
        prev.filter((id) => !currentPageUserIds.includes(id))
      );
    } else {
      setSelectedUsers((prev) => [
        ...new Set([...prev, ...currentPageUserIds]),
      ]);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedUsers.length === 0) {
      toast.error(TOAST_MESSAGES.USERS_NOT_SELECTED);
      return;
    }

    const success = await bulkDeleteUsers(selectedUsers);
    if (success) {
      setSelectedUsers([]);
      setHasMore(true);
      await loadUsers(true);
    }
  };

  useEffect(() => {
    if (projectId) {
      loadUsers(true);
    }
  }, [projectId, filters.search]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchText }));
      onPageChange(1);
    }, 600);

    return () => clearTimeout(handler);
  }, [searchText]);

  useEffect(() => {
    setSelectedUsers((prev) =>
      prev.filter((id) => projectUsers.some((u) => u.id === id))
    );
  }, [projectUsers]);

  useEffect(() => {
    if (showUsers) {
      fetchUsers();
    }
  }, [showUsers]);

  return (
    <div className="flex flex-col h-full overflow-hidden p-2">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-semibold text-primary">Project Users</h3>
        <RoleValidator
          allowedRoles={[
            ROLES.MANAGING_DIRECTOR,
            ROLES.HEAD_OFFICE,
            ROLES.GOVERNMENT_OFFICER,
          ]}
          mode="disable"
        >
          <button
            onClick={() => {
              setShowUsers(true);
              setSelectedUserId("");
              setIsUserDropdownOpen(false);
            }}
            className="flex items-center gap-1 bg-primary text-white px-3 py-1 rounded-md text-sm hover:bg-blue-600 transition-colors"
          >
            <MdAdd size={18} /> Add User
          </button>
        </RoleValidator>
      </div>

      <div className="flex flex-wrap justify-between items-center gap-3 mb-3">
        <div className="relative flex items-center w-full md:w-[50%]">
          <MdSearch size={20} className="absolute left-3 text-gray-500" />
          <input
            type="text"
            name="search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search Users..."
            className="w-full rounded-md border border-gray-300 bg-white text-sm pl-9 pr-8 py-1
                   placeholder-gray-400 focus:ring-1 focus:border-blue-500 outline-none transition duration-150"
          />
          {searchText && (
            <button
              type="button"
              onClick={() => setSearchText("")}
              className="absolute right-3 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Download */}
          <Tooltip.Provider>
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <RoleValidator
                  allowedRoles={[
                    ROLES.MANAGING_DIRECTOR,
                    ROLES.HEAD_OFFICE,
                    ROLES.GOVERNMENT_OFFICER,
                  ]}
                  mode="disable"
                >
                  <button
                    onClick={() => {
                      const link = document.createElement("a");
                      link.href = ExcelFile;
                      link.download = "sample_user_mapping.xlsx";
                      link.click();
                    }}
                    className="px-4 py-2 rounded-md border hover:bg-gray-100"
                  >
                    <FiDownload size={16} className="text-green-600" />
                  </button>
                </RoleValidator>
              </Tooltip.Trigger>
              <Tooltip.Content
                side="top"
                className="rounded bg-gray-800 px-2 py-1 text-xs text-white shadow-md"
              >
                Download sample excel
                <Tooltip.Arrow className="fill-gray-800" />
              </Tooltip.Content>
            </Tooltip.Root>
          </Tooltip.Provider>

          {/* Upload */}
          <Tooltip.Provider>
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <RoleValidator
                  allowedRoles={[
                    ROLES.MANAGING_DIRECTOR,
                    ROLES.HEAD_OFFICE,
                    ROLES.GOVERNMENT_OFFICER,
                  ]}
                  mode="disable"
                >
                  <FileUploader
                    label=""
                    helperText=""
                    accept=".xlsx,.xls"
                    singleButton="true"
                    btnText={<FiUpload size={16} className="text-blue-500" />}
                    onUploadClick={(file) => handleUpload(file)}
                  />
                </RoleValidator>
              </Tooltip.Trigger>
              <Tooltip.Content
                side="top"
                className="rounded bg-gray-800 px-2 py-1 text-xs text-white shadow-md"
              >
                Upload user excel
                <Tooltip.Arrow className="fill-gray-800" />
              </Tooltip.Content>
            </Tooltip.Root>
          </Tooltip.Provider>
        </div>
      </div>

      {failedUsers.length > 0 && (
        <div className="mb-4 py-2 px-3 border border-red-300 bg-red-50 rounded-md relative">
          <div className="flex justify-between items-center mb-1">
            <h3 className="text-red-700 text-xs font-semibold">
              Failed Users to Upload ({failedUsers.length}/{totalRows})
            </h3>
            <button
              onClick={() => setFailedUsers([])}
              className="text-red-500 hover:text-red-700 font-bold"
              aria-label="Clear Failed Users"
            >
              ✕
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2 text-[10px] text-red-600 max-h-40 overflow-y-auto">
            {failedUsers.slice(0, 4).map((user, idx) => (
              <div key={idx} className="truncate">
                {user.name} - {user.user_mobile}
              </div>
            ))}

            {failedUsers.length > 4 && (
              <div className="col-span-2 text-center font-bold text-red-600">. . .</div>
            )}
          </div>
        </div>
      )}

      {projectUsers.length > 0 && (
        <div className="flex items-center gap-4 mb-2 px-1">
          <input
            type="checkbox"
            checked={
              projectUsers.length > 0 &&
              projectUsers.every((u) => selectedUsers.includes(u.id))
            }
            onChange={toggleSelectAll}
            className="rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
          />
          <span className="text-xs font-semibold text-gray-700">
            Select All
          </span>

          {selectedUsers.length > 0 && (
            <>
              <button
                onClick={handleBulkDelete}
                className="bg-red-500 text-white text-xs px-3 py-1 rounded-md hover:bg-red-600"
              >
                Remove
              </button>
              <span className="text-xs text-primary font-semibold">
                {selectedUsers.length} / {totalUserCount} selected
              </span>
            </>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center min-h-[25vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-blue-500"></div>
        </div>
      ) : projectUsers.length === 0 ? (
        <div className="text-center text-gray-500 py-6 min-h-[25vh]">
          No users found.
        </div>
      ) : (
        //? Implement Failed users to display
        <div
          id="project-users-scroll"
          className="flex-1 overflow-y-auto custom-scrollbar
             min-h-[25vh] max-h-[45vh]"
        >
          <InfiniteScroll
            dataLength={projectUsers.length}
            next={() => loadUsers(false)}
            hasMore={hasMore}
            loader={
              <div className="flex justify-center items-center py-2">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-blue-500"></div>
              </div>
            }
            endMessage={
              projectUsers.length > 0 ? (
                <p className="text-center text-gray-400 text-xs py-2">
                  All users loaded
                </p>
              ) : null
            }
            scrollableTarget="project-users-scroll"
          >
            <ul className="divide-y divide-gray-200 dark:divide-gray-700 w-full">
              {projectUsers.map((user) => (
                <li key={user.id} className="py-2 px-1">
                  <div className="grid grid-cols-9 items-center gap-2">
                    <div className="col-span-2 flex justify-start items-center gap-5">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => toggleUserSelection(user.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
                      />
                      <ProfilePhoto
                        src={user?.user?.profile_photo}
                        alt={user?.user?.first_name}
                        firstName={user?.user?.first_name}
                        lastName={user?.user?.last_name}
                        size="xs"
                      />
                    </div>

                    <div className="col-span-3 flex flex-col items-start gap-1">
                      <span className="text-xs font-semibold text-gray-900 dark:text-white truncate max-w-[95%]">
                        {user.user.first_name} {user.user.last_name}
                      </span>
                      <span className="text-[10px] text-gray-600 dark:text-gray-300">
                        +91-{user.user.mobile}
                      </span>
                    </div>

                    <div className="col-span-3 flex justify-center">
                      <span
                        className={`px-1 py-1 text-[9px] font-medium rounded-full ${
                          ROLE_COLORS[user?.role]
                        }`}
                      >
                        {toProperCase(user?.role)}
                      </span>
                    </div>

                    <div className="col-span-1 flex justify-end">
                      <RoleValidator
                        allowedRoles={[
                          ROLES.MANAGING_DIRECTOR,
                          ROLES.HEAD_OFFICE,
                          ROLES.GOVERNMENT_OFFICER,
                          ROLES.PROJECT_MANAGER,
                        ]}
                        mode="disable"
                      >
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="p-2 rounded-full hover:bg-gray-100"
                          disabled={deletingUserId === user.id}
                        >
                          <Trash2 size={16} className="text-red-500" />
                        </button>
                      </RoleValidator>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </InfiniteScroll>
        </div>
      )}

      {/* Add User Modal (unchanged) */}
      {showUsers && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 relative transform transition-transform duration-300 scale-100 m-4">
            <button
              onClick={() => {
                setShowUsers(false);
                setSelectedUserId("");
              }}
              aria-label="Close"
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              ✕
            </button>

            <h2 className="text-2xl font-bold mb-6 text-gray-800">Add User</h2>

            <form onSubmit={handleAddUser} className="space-y-4">
              <ScrollableDropdown
                label="Select User"
                options={usersList.map((u) => ({
                  label: `${u.first_name} ${u.last_name}`,
                  value: u.id,
                }))}
                value={selectedUserId}
                onChange={setSelectedUserId}
                isOpen={isUserDropdownOpen}
                onToggle={setIsUserDropdownOpen}
              />

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-primary hover:bg-blue-600 text-white font-semibold py-2 rounded-lg shadow-md transition-colors duration-200 disabled:opacity-50"
              >
                {submitting ? "Submitting..." : "Submit"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersTab;
